/**
 * Shared reCAPTCHA v3 token minting.
 *
 * Extracted from ContactFormController when the contact-details reveal became a
 * second consumer. Both the form submit and the reveal mint their tokens here,
 * each under its own action name, so neither token is replayable against the
 * other endpoint.
 */

/** The reCAPTCHA script is configured but did not load or would not run. */
export class RecaptchaUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RecaptchaUnavailableError';
  }
}

function waitForRecaptcha(timeoutMs: number): Promise<Window['grecaptcha'] | undefined> {
  return new Promise((resolve) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }

    const startedAt = Date.now();
    const poll = window.setInterval(() => {
      if (window.grecaptcha) {
        window.clearInterval(poll);
        resolve(window.grecaptcha);
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(poll);
        resolve(undefined);
      }
    }, 100);
  });
}

/**
 * Mints a reCAPTCHA v3 token for the given action.
 *
 * Returns null only when no site key is given, which lets development run
 * without keys; the server then decides whether a missing token is acceptable.
 *
 * When a key *is* given, a failure throws rather than resolving null. Posting a
 * null token to a server that requires one produces a 403 whose real cause is
 * invisible from either side — the failure that prompted this was reported only
 * as "missing-token" in a server log, when the actual problem was in the
 * browser.
 */
export async function mintRecaptchaToken(
  siteKey: string | undefined,
  action: string,
): Promise<string | null> {
  if (!siteKey) {
    return null;
  }

  // The script tag is async and deferred, so a visitor who acts promptly can
  // beat it. Wait briefly rather than treating a slow load as a hard failure.
  const grecaptcha = await waitForRecaptcha(5000);

  if (!grecaptcha) {
    throw new RecaptchaUnavailableError('grecaptcha did not load within 5s');
  }

  return new Promise<string>((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(siteKey, { action })
        .then(resolve)
        .catch((cause: unknown) => {
          // The most common cause is a site key not registered for this
          // hostname, which reCAPTCHA reports here and nowhere else. Surface it
          // rather than swallowing it.
          reject(
            new RecaptchaUnavailableError(
              `grecaptcha.execute failed: ${cause instanceof Error ? cause.message : String(cause)}`,
            ),
          );
        });
    });
  });
}
